import type { FieldConfig, OperationOption, ValueState } from '../../types'

import { Operators } from '../../constants'
import { isObject, objectMap } from '../../utils/utils'
import FieldController from '../Controller'

export default class PercentController extends FieldController {
	constructor(config: FieldConfig) {
		super(config)

		this.getInitialValue = config.getInitialValue || this.getInitialValue
		this.getFilterTypes = config.getFilterTypes || this.getFilterTypes
		this.note = config.note
		this.validate = config.validate || this.defaultValidate
	}

	hasValue = ({ operation, value }: any) => {
		if (operation === Operators.IS_EMPTY) return true

		let bool = typeof value === 'number'

		if (isObject(value)) {
			Object.values(value).forEach((v) => {
				bool = typeof v === 'number'
			})
		}

		return bool
	}

	getInitialValue = () => ({
		operation: Operators.EQ,
		value: '',
	})

	getFilterTypes = (): OperationOption[] => [
		{ operation: Operators.EQ, label: 'equal', hasInput: true },
		{ operation: Operators.NE, label: 'not equal', hasInput: true },
		{ operation: Operators.GT, label: 'greater than', hasInput: true },
		{
			operation: Operators.GTE,
			label: 'greater or equal than',
			hasInput: true,
		},
		{ operation: Operators.LT, label: 'less than', hasInput: true },
		{ operation: Operators.LTE, label: 'less or equal than', hasInput: true },
		{ operation: Operators.BETWEEN, label: 'between', hasInput: true },
		{ operation: Operators.IS_EMPTY, label: 'has no value' },
		{ operation: Operators.IS_NOT_EMPTY, label: 'has value' },
	]

	defaultValidate = ({ operation, value }: any) => {
		let result: string | null = null
		const nameMap = { lt: 'to', gt: 'from' }

		if (operation === Operators.IS_EMPTY) {
			return result
		}

		if (isObject(value)) {
			// make sure both values are present
			if (value.lt === undefined || value.gt === undefined) {
				return 'Both inputs are required.'
			}
			if (value.lt < 0 || value.gt < 0) {
				return 'Only positive values are available.'
			}

			// check for a valid range
			if (value.lt <= value.gt) {
				return 'Invalid range; the second input must be greater than the first.'
			}

			objectMap(value, (val, key) => {
				const r = validateInput(
					this.label,
					val,
					nameMap[key as keyof typeof nameMap]
				)
				if (r) result = r

				return null
			})
		} else {
			result = validateInput(this.label, value)
		}

		return result
	}
	formatState = ({ operation, value }: ValueState) => {
		if ([Operators.IS_EMPTY, Operators.IS_NOT_EMPTY].includes(operation)) {
			return {
				operation,
				value: '',
			}
		}

		return {
			operation,
			value,
		}
	}
}

const validateInput = (label: string, _value: any, name?: any) => {
	let result: string | null = null
	const prefix = name ? `${label} "${name}"` : label
	const value = Number(_value)
	if (_value === '' || _value === null || _value === undefined) {
		result = `${prefix} must be a number`
	} else if (Number.isNaN(value)) {
		result = `${prefix} must be a number`
	} else if (!Number.isFinite(value)) {
		result = `${prefix} must be a whole number`
	} else if (value < 0) {
		result = `${prefix} must be a positive number`
	}

	return result
}
